"use client"
import type {
	FormFieldBlock,
	Form as FormType,
} from "@payloadcms/plugin-form-builder/types"

import { useRouter } from "next/navigation"
import React, { useCallback, useState } from "react"
import { useForm, FormProvider } from "react-hook-form"
import RichText from "@/components/RichText"
import { Button } from "@/components/ui/button"
import type { DefaultTypedEditorState } from "@payloadcms/richtext-lexical"

import { fields } from "./fields"
import { getClientSideURL } from "@/utilities/getURL"

export type FormBlockType = {
	blockName?: string
	blockType?: "formBlock"
	enableIntro: boolean
	form: FormType
	introContent?: DefaultTypedEditorState
}

export const FormBlock: React.FC<
	{
		id?: string
	} & FormBlockType
> = (props) => {
	const {
		enableIntro,
		form: formFromProps,
		form: {
			id: formID,
			confirmationMessage,
			confirmationType,
			redirect,
			submitButtonLabel,
		} = {},
		introContent,
	} = props

	const formMethods = useForm({
		defaultValues: formFromProps.fields,
	})
	const {
		control,
		formState: { errors },
		handleSubmit,
		register,
	} = formMethods

	const [isLoading, setIsLoading] = useState(false)
	const [hasSubmitted, setHasSubmitted] = useState<boolean>()
	const [error, setError] = useState<
		{ message: string; status?: string } | undefined
	>()
	const router = useRouter()

	const onSubmit = useCallback(
		(data: FormFieldBlock[]) => {
			let loadingTimerID: ReturnType<typeof setTimeout>
			const submitForm = async () => {
				setError(undefined)

				const dataToSend = Object.entries(data).map(([name, value]) => ({
					field: name,
					value,
				}))

				// delay loading indicator by 1s
				loadingTimerID = setTimeout(() => {
					setIsLoading(true)
				}, 1000)

				try {
					const req = await fetch(
						`${getClientSideURL()}/api/form-submissions`,
						{
							body: JSON.stringify({
								form: formID,
								submissionData: dataToSend,
							}),
							headers: {
								"Content-Type": "application/json",
							},
							method: "POST",
						},
					)

					const res = await req.json()

					clearTimeout(loadingTimerID)

					if (req.status >= 400) {
						setIsLoading(false)

						setError({
							message: res.errors?.[0]?.message || "Internal Server Error",
							status: res.status,
						})

						return
					}

					setIsLoading(false)
					setHasSubmitted(true)

					if (confirmationType === "redirect" && redirect) {
						const { url } = redirect

						const redirectUrl = url

						if (redirectUrl) router.push(redirectUrl)
					}
				} catch (err) {
					console.warn(err)
					setIsLoading(false)
					setError({
						message: "Something went wrong.",
					})
				}
			}

			void submitForm()
		},
		[router, formID, redirect, confirmationType],
	)

	return (
		<div className="container mx-auto px-4 py-8 lg:py-12">
			{enableIntro && introContent && !hasSubmitted && (
				<RichText
					className="mb-8 lg:mb-12 text-center"
					data={introContent}
					enableGutter={false}
				/>
			)}
			{/* Modern card with shadow, rounded corners, white background */}
			<div className="max-w-4xl mx-auto bg-white dark:bg-card rounded-xl shadow-lg border border-border/50 p-6 lg:p-10">
				<FormProvider {...formMethods}>
					{!isLoading && hasSubmitted && confirmationType === "message" && (
						<RichText data={confirmationMessage} />
					)}
					{isLoading && !hasSubmitted && <p>Loading, please wait...</p>}
					{error && (
						<div>{`${error.status || "500"}: ${error.message || ""}`}</div>
					)}
					{!hasSubmitted && (
						<form id={formID} onSubmit={handleSubmit(onSubmit)}>
							{/* Flex container with wrap and gaps for side-by-side fields */}
							<div className="flex flex-wrap -mx-3 gap-y-4 mb-6">
								{formFromProps &&
									formFromProps.fields &&
									formFromProps.fields?.map((field, index) => {
										// eslint-disable-next-line @typescript-eslint/no-explicit-any
										const Field: React.FC<any> =
											fields?.[field.blockType as keyof typeof fields]
										if (Field) {
											// Use name if available, otherwise use blockType + index for unique key
											const fieldKey =
												(field as any).name || `${field.blockType}-${index}`
											return (
												<Field
													key={fieldKey}
													form={formFromProps}
													{...field}
													{...formMethods}
													control={control}
													errors={errors}
													register={register}
												/>
											)
										}
										return null
									})}
							</div>

							<div className="flex justify-center mt-8">
								<Button
									form={formID}
									type="submit"
									variant="default"
									size="lg"
									className="min-w-[200px]"
								>
									{submitButtonLabel}
								</Button>
							</div>
						</form>
					)}
				</FormProvider>
			</div>
		</div>
	)
}
